import React from 'react';
import { Box, Typography, Button, Badge, IconButton, Tooltip } from '@mui/material';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { StatusChip } from '../ui';
import { useOrgFeatureFlag } from '../../hooks/useOrgFeatureFlags';
import { PhotoLibrary } from '@mui/icons-material';
import { CameraWithPricing, toAbsoluteImageUrl, formatDate, formatRelativeDate, formatStockStatus } from '../../utils/productDetailHelpers';
import { tokens } from '../../theme/tokens';
import { countryOfOriginFromProduct, japanArticleNumber } from '../../utils/productIdentityExtras';

interface IdentifierRow {
  label: string;
  value: string;
  /** Set on a row whose value is an estimate (source of the estimate). */
  estimateSource?: string;
}

interface TimelineRow {
  label: string;
  value: string;
  note?: string | null;
}

const PUSHABLE_WEIGHT_VALUE_RE = /([\d.]+)\s*(g|oz|lb|lbs|kg)/i;

/**
 * True when the catalog already has a weight the storefront push would use.
 * Mirrors `hasAnyPushableWeight` for the Product shape (nested physical specs
 * or flattened adapter keys such as `physical.weight`).
 */
function hasPushableCatalogWeight(product: CameraWithPricing): boolean {
  if (Number(product.shippingDimensions?.weight) > 0) return true;
  if (Number(product.dimensions?.weight) > 0) return true;
  const specs = product.specifications as Record<string, unknown> | undefined | null;
  if (!specs) return false;
  const physical = specs.physical as Record<string, unknown> | undefined;
  for (const raw of [physical?.weight, physical?.weightWithBattery]) {
    if (typeof raw === 'string' && PUSHABLE_WEIGHT_VALUE_RE.test(raw)) return true;
  }
  for (const [key, value] of Object.entries(specs)) {
    if (typeof value !== 'string') continue;
    const k = key.toLowerCase();
    const isWeightKey =
      k === 'physical.weight'
      || k === 'physical.weightwithbattery'
      || /(?:^|[.\s_-])(weight|mass)(?:$|[.\s_(-])/i.test(k)
      || /size\s*(?:and|&)\s*weight/i.test(k);
    if (isWeightKey && PUSHABLE_WEIGHT_VALUE_RE.test(value)) return true;
  }
  return false;
}

/**
 * Estimated packaged weight / box rows. Shown only under flag
 * `shipping_weight_fallback`, and only when the product has no weight of its
 * own, matching what the storefront push would send.
 */
function buildShippingEstimateRows(
  product: CameraWithPricing,
  labels: { weight: string; dimensions: string },
): IdentifierRow[] {
  const est = product.estimatedShippingDimensions;
  if (!est || !(Number(est.weight) > 0)) return [];
  // `wouldPush` comes from the API (the push's own rule); the local check covers pages that receive the raw product.
  if (est.wouldPush === false || hasPushableCatalogWeight(product)) return [];
  const rows: IdentifierRow[] = [
    { label: labels.weight, value: `${est.weight} ${est.weightUnit || 'g'}`, estimateSource: est.source || 'category' },
  ];
  const dims = [est.length, est.width, est.height].filter((v): v is number => v != null && Number.isFinite(Number(v)));
  if (dims.length === 3) {
    rows.push({ label: labels.dimensions, value: `${dims.join(' × ')} ${est.unit || 'mm'}`, estimateSource: est.source || 'category' });
  }
  return rows;
}

function buildIdentifiers(product: CameraWithPricing): IdentifierRow[] {
  const rows: IdentifierRow[] = [];
  if (product.sku) {
    rows.push({
      label: 'SKU / Model',
      value: product.model && product.model !== product.sku ? `${product.sku} / ${product.model}` : product.sku,
    });
  } else if (product.model) {
    rows.push({ label: 'Model', value: product.model });
  }
  if (product.mpn) rows.push({ label: 'MPN', value: product.mpn });
  if (product.upc) rows.push({ label: 'UPC', value: product.upc });
  if (product.ean && product.ean !== product.upc) rows.push({ label: 'EAN', value: product.ean });
  const jan = japanArticleNumber(product);
  if (jan && jan !== product.upc && jan !== product.ean) rows.push({ label: 'JAN', value: jan });
  const origin = countryOfOriginFromProduct(product);
  if (origin) rows.push({ label: 'Country of origin', value: origin });
  if (product.source) rows.push({ label: 'Source', value: product.source });
  if (product.dimensions?.weight != null) {
    rows.push({ label: 'Weight', value: `${product.dimensions.weight} ${product.dimensions.weightUnit || 'g'}` });
  }
  const d = product.dimensions;
  const dims = [d?.length, d?.width, d?.height].filter((v): v is number => v != null && Number.isFinite(Number(v)));
  if (dims.length > 0) {
    rows.push({ label: 'Dimensions', value: `${dims.join(' × ')} ${d?.unit || 'mm'}` });
  }
  return rows;
}

function buildTimeline(product: CameraWithPricing): TimelineRow[] {
  const rows: TimelineRow[] = [];
  if (product.launchDate) {
    const formatted = formatDate(product.launchDate) || product.launchDate;
    rows.push({ label: 'Launched', value: formatted });
  }
  if (product.firstImportedAt) {
    rows.push({
      label: 'First imported',
      value: formatDate(product.firstImportedAt) || '—',
      note: formatRelativeDate(product.firstImportedAt) ? `(${formatRelativeDate(product.firstImportedAt)})` : null,
    });
  }
  if (product.lastScrapedAt) {
    rows.push({
      label: 'Listing updated',
      value: formatDate(product.lastScrapedAt) || '—',
      note: formatRelativeDate(product.lastScrapedAt) ? `(${formatRelativeDate(product.lastScrapedAt)})` : null,
    });
  }
  if (product.manufacturerStock?.status && product.manufacturerStock.status !== 'unknown') {
    const checked = formatRelativeDate(product.manufacturerStock.checkedAt) || formatDate(product.manufacturerStock.checkedAt);
    rows.push({
      label: `Stock at ${product.brand}`,
      value: product.manufacturerStock.label || formatStockStatus(product.manufacturerStock.status),
      note: checked ? `checked ${checked} · not pushed` : 'not pushed',
    });
  }
  if (product.archivedAt) {
    rows.push({
      label: 'Archived',
      value: formatDate(product.archivedAt) || '—',
      note: formatRelativeDate(product.archivedAt) ? `(${formatRelativeDate(product.archivedAt)})` : null,
    });
  }
  return rows;
}

interface ProductGalleryAndIdentifiersProps {
  product: CameraWithPricing;
  onOpenGallery: (images: string[], title: string, index: number) => void;
}

export function ProductGalleryAndIdentifiers({ product, onOpenGallery }: ProductGalleryAndIdentifiersProps) {
  const images = product.images ?? [];
  const openGallery = (index: number) => {
    const urls = images.map((img) => toAbsoluteImageUrl(img)).filter((u): u is string => !!u);
    onOpenGallery(urls, product.name || product.model, index);
  };

  const { t } = useAppTranslation();
  const { enabled: shippingEstimateOn } = useOrgFeatureFlag('shipping_weight_fallback');
  const identifiers = [
    ...buildIdentifiers(product),
    ...(shippingEstimateOn
      ? buildShippingEstimateRows(product, {
          weight: t('products.shippingEstimate.weight'),
          dimensions: t('products.shippingEstimate.dimensions'),
        })
      : []),
  ];
  const timeline = buildTimeline(product);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      <Box sx={{ position: 'relative', height: { xs: 220, sm: 300 }, borderRadius: '12px', bgcolor: tokens.surface.sunken, border: `1px solid ${tokens.line.hairline}`, overflow: 'hidden' }}>
        {images[0] ? (
          <Box
            component="button"
            type="button"
            onClick={() => openGallery(0)}
            aria-label={`Open photo gallery for ${product.model || 'this product'}`}
            sx={{
              display: 'block',
              width: '100%',
              height: '100%',
              p: 1.5,
              border: 'none',
              bgcolor: 'transparent',
              cursor: 'pointer',
              boxSizing: 'border-box',
            }}
          >
            <Box
              component="img"
              src={toAbsoluteImageUrl(images[0])}
              alt={product.model}
              sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
            />
          </Box>
        ) : (
          <Box sx={{ width: '100%', height: '100%', display: 'grid', placeItems: 'center' }}>
            <Typography sx={{ fontSize: '12.5px', color: tokens.ink.faint }}>No product photo</Typography>
          </Box>
        )}
        {images.length > 1 && (
          <Badge badgeContent={images.length} color="primary" sx={{ position: 'absolute', top: 10, right: 10 }}>
            <IconButton
              size="small"
              onClick={() => openGallery(0)}
              aria-label={`Open photo gallery (${images.length} photos)`}
              sx={{ bgcolor: 'rgba(15,23,42,0.7)', color: 'common.white', '&:hover': { bgcolor: 'rgba(15,23,42,0.85)' } }}
            >
              <PhotoLibrary sx={{ fontSize: 16 }} />
            </IconButton>
          </Badge>
        )}
      </Box>

      {images.length > 1 && (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 0.75 }}>
          {images.slice(0, 6).map((image, index) => (
            <Box
              key={index}
              component="button"
              type="button"
              onClick={() => openGallery(index)}
              aria-label={`Open photo ${index + 1} of ${images.length}`}
              sx={{
                position: 'relative',
                height: 50,
                p: 0,
                display: 'grid',
                placeItems: 'center',
                borderRadius: '8px',
                bgcolor: tokens.surface.sunken,
                border: '1px solid',
                borderColor: index === 0 ? tokens.accent.border : tokens.line.hairline,
                overflow: 'hidden',
                cursor: 'pointer',
              }}
            >
              <Box component="img" src={toAbsoluteImageUrl(image)} alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              {index === 5 && images.length > 6 && (
                <Box sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', bgcolor: 'rgba(15,23,42,0.64)' }}>
                  <Typography sx={{ color: 'common.white', fontSize: '12px', fontWeight: 600 }}>+{images.length - 6}</Typography>
                </Box>
              )}
            </Box>
          ))}
        </Box>
      )}

      {images.length > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <Typography sx={{ fontSize: '12px', color: tokens.ink.faint, fontVariantNumeric: 'tabular-nums' }}>
            {images.length} photo{images.length === 1 ? '' : 's'}
          </Typography>
          {images.length > 1 && (
            <Button
              onClick={() => openGallery(0)}
              sx={{ p: 0, minWidth: 0, textTransform: 'none', fontSize: '12.5px', fontWeight: 600, color: 'primary.main' }}
            >
              View all photos
            </Button>
          )}
        </Box>
      )}

      {identifiers.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1, pt: 2, borderTop: `1px solid ${tokens.line.hairline}` }}>
          <Typography sx={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: tokens.ink.faint }}>
            Identifiers
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {identifiers.map((row) => (
              <Box
                key={row.label}
                sx={{ display: 'grid', gridTemplateColumns: { xs: '96px 1fr', md: '96px 1fr' }, gap: 1.5, alignItems: 'baseline', py: '7px', borderBottom: `1px solid ${tokens.surface.sunken}` }}
              >
                <Typography sx={{ fontSize: '12px', color: tokens.ink.faint }}>{row.label}</Typography>
                <Typography sx={{ fontSize: '13px', color: 'text.primary', fontVariantNumeric: 'tabular-nums', wordBreak: 'break-word', minWidth: 0 }}>
                  {row.value}
                  {row.estimateSource && (
                    <Tooltip
                      title={row.estimateSource === 'gemini' ? t('products.shippingEstimate.sourceGemini') : t('products.shippingEstimate.sourceCategory')}
                    >
                      <Box component="span" sx={{ ml: 1, display: 'inline-flex', verticalAlign: 'middle' }}>
                        <StatusChip label={t('products.shippingEstimate.estimated')} tone="caution" />
                      </Box>
                    </Tooltip>
                  )}
                </Typography>
              </Box>
            ))}
          </Box>
          {timeline.length > 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              {timeline.map((row) => (
                <Box key={row.label} sx={{ display: 'flex', alignItems: 'baseline', gap: 1, fontSize: '12.5px', flexWrap: 'wrap' }}>
                  <Typography component="span" sx={{ color: tokens.ink.faint, fontSize: 'inherit' }}>{row.label}</Typography>
                  <Typography component="span" sx={{ color: 'text.primary', fontVariantNumeric: 'tabular-nums', fontSize: 'inherit' }}>{row.value}</Typography>
                  {row.note && <Typography component="span" sx={{ color: tokens.ink.faint, fontSize: 'inherit' }}>{row.note}</Typography>}
                </Box>
              ))}
            </Box>
          )}
        </Box>
      )}

      {product.tags && product.tags.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mt: 1, pt: 2, borderTop: `1px solid ${tokens.line.hairline}` }}>
          <Typography sx={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: tokens.ink.faint }}>
            Tags
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
            {product.tags.map((tag) => (
              <Box
                key={tag}
                component="span"
                sx={{ display: 'inline-flex', alignItems: 'center', height: 23, px: '8px', borderRadius: '7px', bgcolor: tokens.surface.tint, color: 'text.secondary', fontSize: '12px' }}
              >
                {tag}
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
}
